import {
    type IGetCreditLinePortfolioOpts,
    type IUpdateCreditLinePortfolioParams,
    addEstatesToCreditLinePortfolio,
    deleteEstatesFromCreditLinePortfolio,
    fetchCreditLineCashflowPrediction,
    fetchCreditLinePortfolio,
    fetchCreditLinePortfolios,
    generateCreditLineBudgetCostsSheet,
    generateCreditLineCashflowPredictions,
    updateCreditLinePortfolio,
} from '@/api/credit-line-portfolio.api';
import { withLoading } from '@/shared';
import type { ICashflowPrediction, ICreditLinePortfolio } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { ref } from 'vue';

export const useCreditLinePortfolios = (opts?: IGetCreditLinePortfolioOpts) => {
    const queryClient = useQueryClient();

    const query = useQuery(
        ['credit-line-portfolios/list', opts],
        async (): Promise<ICreditLinePortfolio[]> => withLoading(async () => fetchCreditLinePortfolios(opts)),
        {
            initialData: [],
        },
    );

    const { mutateAsync: addEstatesMutation } = useMutation(
        async ({ creditLinePortfolioId, estateIds }: { creditLinePortfolioId: number; estateIds: number[] }) =>
            addEstatesToCreditLinePortfolio(creditLinePortfolioId, estateIds),
        {
            onSuccess: updatedCreditLinePortfolio => {
                queryClient.invalidateQueries(['credit-line-portfolio', updatedCreditLinePortfolio.creditLinePortfolioId, opts]);
            },
        },
    );

    return { ...query, addEstatesToCreditLinePortfolio: addEstatesMutation };
};

export const useCreditLinePortfolio = (creditLinePortfolioId: number, opts?: IGetCreditLinePortfolioOpts, fetchEnabled = true) => {
    const queryClient = useQueryClient();

    const query = useQuery(
        ['credit-line-portfolios/single', creditLinePortfolioId, opts],
        async (): Promise<ICreditLinePortfolio> => withLoading(async () => fetchCreditLinePortfolio(creditLinePortfolioId)),
        {
            enabled: fetchEnabled,
            initialData: null,
        },
    );

    const { mutateAsync: updateMutation } = useMutation(
        async (params: IUpdateCreditLinePortfolioParams) => updateCreditLinePortfolio(creditLinePortfolioId, params),
        {
            onSuccess: updatedCreditLinePortfolio => {
                queryClient.setQueryData(['credit-line-portfolios/single', creditLinePortfolioId, opts], (prevData: ICreditLinePortfolio) => {
                    return { ...prevData, ...updatedCreditLinePortfolio };
                });
            },
        },
    );

    const { mutateAsync: deleteEstatesMutation } = useMutation(
        async (estateIds: number[]) => deleteEstatesFromCreditLinePortfolio(creditLinePortfolioId, estateIds),
        {
            onSuccess: (_, estateIds) => {
                queryClient.setQueryData(['credit-line-portfolios/single', creditLinePortfolioId, opts], (prevData: ICreditLinePortfolio) => {
                    const filteredEstates = prevData.estates.filter(estate => !estateIds.includes(estate.estateId));
                    return { ...prevData, estates: filteredEstates };
                });
            },
        },
    );

    const { mutateAsync: generateBudgetCostsGoogleSheetMutation } = useMutation(async () => generateCreditLineBudgetCostsSheet(creditLinePortfolioId), {
        onSuccess: () => {
            queryClient.invalidateQueries(['credit-line-portfolios/single', creditLinePortfolioId, opts]);
        },
    });

    return {
        ...query,
        update: updateMutation,
        generateBudgetCostsGoogleSheet: generateBudgetCostsGoogleSheetMutation,
        deleteEstates: deleteEstatesMutation,
    };
};

export const useCreditLineCashflowPrediction = (creditLinePortfolioId, { group }) => {
    const groupQuery = ref(group);
    const queryClient = useQueryClient();

    const query = useQuery(
        ['credit-line-portfolios/cashflowPrediction', creditLinePortfolioId, groupQuery],
        async (): Promise<ICashflowPrediction> => withLoading(async () => fetchCreditLineCashflowPrediction(creditLinePortfolioId, groupQuery.value)),
        {
            initialData: null,
        },
    );

    const { mutateAsync: generateGoogleSheetMutation } = useMutation(
        async () => generateCreditLineCashflowPredictions(query?.data?.value?.cashflowPredictionId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['credit-line-portfolios/cashflowPrediction']);
            },
        },
    );

    return { ...query, generateGoogleSheet: generateGoogleSheetMutation };
};
